// src/components/PushNotificationPrompt.tsx
import React, { useState, useEffect } from 'react';
import { FaBell, FaBellSlash, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  requestNotificationPermission, 
  isNotificationsEnabled,
  showPermissionPrompt 
} from '../lib/onesignal';
import { getCurrentUser } from '../lib/auth';

interface PushNotificationPromptProps {
  className?: string;
}

export default function PushNotificationPrompt({ className = '' }: PushNotificationPromptProps) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    // Don't show the web prompt if we're inside the Android WebView app
    if ((window as any).AndroidOneSignal) {
      setShowPrompt(false);
      return;
    }

    // Check login status first
    const user = getCurrentUser();
    setIsLoggedIn(!!user?.id);
    
    if (user?.id) {
      checkNotificationStatus();
    }
  }, []);

  // Re-check when user logs in (listen for storage changes)
  useEffect(() => {
    const handleStorageChange = () => {
      const user = getCurrentUser();
      setIsLoggedIn(!!user?.id);
      if (user?.id) {
        checkNotificationStatus();
      } else {
        setShowPrompt(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const checkNotificationStatus = async () => {
    // Check if user has already dismissed
    const wasDismissed = localStorage.getItem('pushNotificationDismissed');
    if (wasDismissed) {
      setDismissed(true);
      return;
    }

    // Check if notifications are enabled
    const enabled = await isNotificationsEnabled();
    setIsEnabled(enabled);

    // Show prompt if not enabled and not dismissed
    if (!enabled) {
      // Wait a bit before showing
      setTimeout(() => setShowPrompt(true), 3000);
    }
  };

  const handleEnableNotifications = async () => {
    setLoading(true);
    try {
      // First try the slidedown prompt
      await showPermissionPrompt();
      
      // If that doesn't work, request directly
      const granted = await requestNotificationPermission();
      setIsEnabled(granted);
      
      if (granted) {
        setShowPrompt(false);
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pushNotificationDismissed', 'true');
  };

  const handleRemindLater = () => {
    setShowPrompt(false);
    // Show again after 24 hours
    localStorage.setItem('pushNotificationRemindAt', String(Date.now() + 24 * 60 * 60 * 1000));
  };

  // Don't show if not logged in, dismissed, enabled, or prompt not triggered
  if (!isLoggedIn || dismissed || isEnabled || !showPrompt) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className={`fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 ${className}`}
      >
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl border border-yellow-400/20 p-5 relative overflow-hidden">
          {/* Background glow */}
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-yellow-400/20 rounded-full blur-3xl" />
          
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-white transition-colors"
            aria-label="Dismiss"
          >
            <FaTimes className="w-4 h-4" />
          </button>

          <div className="relative z-10">
            {/* Icon */}
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center mb-4 shadow-lg">
              <FaBell className="w-6 h-6 text-black" />
            </div>

            {/* Content */}
            <h3 className="text-lg font-bold text-white mb-2">
              Stay Updated!
            </h3>
            <p className="text-gray-300 text-sm mb-4">
              Get notified about friend requests, new messages, and when new songs or stories are added.
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleEnableNotifications}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-medium rounded-xl transition-all duration-200 disabled:opacity-50"
              >
                {loading ? (
                  <span className="animate-spin">⏳</span>
                ) : (
                  <FaBell className="w-4 h-4" />
                )}
                <span>Enable</span>
              </button>
              <button
                onClick={handleRemindLater}
                className="px-4 py-2.5 text-gray-400 hover:text-white font-medium rounded-xl transition-colors"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * Settings toggle for notifications
 */
export function NotificationSettingsToggle() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    const enabled = await isNotificationsEnabled();
    setIsEnabled(enabled);
    setLoading(false);
  };

  const handleToggle = async () => {
    if (!isEnabled) {
      setLoading(true);
      const granted = await requestNotificationPermission();
      setIsEnabled(granted);
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
      <div className="flex items-center gap-3">
        {isEnabled ? (
          <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
            <FaBell className="w-5 h-5 text-green-400" />
          </div>
        ) : (
          <div className="w-10 h-10 bg-gray-500/20 rounded-lg flex items-center justify-center">
            <FaBellSlash className="w-5 h-5 text-gray-400" />
          </div>
        )}
        <div>
          <p className="font-medium text-white">Push Notifications</p>
          <p className="text-sm text-gray-400">
            {isEnabled ? 'Notifications are enabled' : 'Enable to stay updated'}
          </p>
        </div>
      </div>
      
      <button
        onClick={handleToggle}
        disabled={loading || isEnabled}
        className={`px-4 py-2 rounded-lg font-medium transition-all ${
          isEnabled
            ? 'bg-green-500/20 text-green-400 cursor-default'
            : 'bg-yellow-400 text-black hover:bg-yellow-500'
        } ${loading ? 'opacity-50 cursor-wait' : ''}`}
      >
        {loading ? '...' : isEnabled ? 'Enabled' : 'Enable'}
      </button>
    </div>
  );
}
